import { SignatureFrame } from '../../../common/pokCore/validation/signatureFrame';

export interface PdfPageSize {
  widthPt: number;
  heightPt: number;
}

export interface FrameRectPercent {
  left: number;
  top: number;
  width: number;
  height: number;
}

const clamp = (value: number, max: number) => Math.max(0, Math.min(value, max));

export const getFrameRectPercent = (
  x: number,
  y: number,
  page: PdfPageSize,
): FrameRectPercent => {
  const anchorX = (x / 100) * page.widthPt;
  const anchorYFromBottom = page.heightPt - (y / 100) * page.heightPt;

  const leftPt = clamp(
    anchorX - SignatureFrame.signatureBoxWidthPt / 2,
    page.widthPt - SignatureFrame.widthPt,
  );
  const bottomPt = clamp(
    anchorYFromBottom - SignatureFrame.heightPt / 2,
    page.heightPt - SignatureFrame.heightPt,
  );
  const topPt = page.heightPt - bottomPt - SignatureFrame.heightPt;

  return {
    left: (leftPt / page.widthPt) * 100,
    top: (topPt / page.heightPt) * 100,
    width: (SignatureFrame.widthPt / page.widthPt) * 100,
    height: (SignatureFrame.heightPt / page.heightPt) * 100,
  };
};

const signaturePositionRangePercent = (
  offsetBeforePt: number,
  offsetAfterPt: number,
  sizePt: number,
) => {
  const min = (offsetBeforePt / sizePt) * 100;
  const max = ((sizePt - offsetAfterPt) / sizePt) * 100;
  return min <= max ? { min, max } : { min: 50, max: 50 };
};

export const fitSignaturePositionToPage = (
  x: number,
  y: number,
  page: PdfPageSize,
) => {
  const half = SignatureFrame.signatureBoxWidthPt / 2;
  const xRange = signaturePositionRangePercent(
    half,
    SignatureFrame.widthPt - half,
    page.widthPt,
  );
  const yRange = signaturePositionRangePercent(
    SignatureFrame.heightPt / 2,
    SignatureFrame.heightPt / 2,
    page.heightPt,
  );
  return {
    x: Math.min(Math.max(x, xRange.min), xRange.max),
    y: Math.min(Math.max(y, yRange.min), yRange.max),
  };
};

// Default position (signature box anchor) for a new frame: the whole frame
// in the middle of the page.
export const getDefaultSignaturePosition = (page: PdfPageSize) => {
  const frameCenterOffsetPt =
    SignatureFrame.widthPt / 2 - SignatureFrame.signatureBoxWidthPt / 2;
  return fitSignaturePositionToPage(
    50 - (frameCenterOffsetPt / page.widthPt) * 100,
    50,
    page,
  );
};

// Size of an image inside one box of the frame, as % of that box: scaled down
// (never up) to fit, like SignPdf does — image pixels are PDF points there.
export const fitImageInBoxPercent = (
  image: { width: number; height: number },
  boxWidthPt: number,
) => {
  const scale = Math.min(
    1,
    boxWidthPt / image.width,
    SignatureFrame.heightPt / image.height,
  );
  return {
    width: ((image.width * scale) / boxWidthPt) * 100,
    height: ((image.height * scale) / SignatureFrame.heightPt) * 100,
  };
};
